/**
 * Home Page
 * =========
 * Each section below uses a deliberately different composition — feature
 * grid, pull-quote spread, full-bleed band, solid colour block — so nine
 * scrolls in a row never feel like the same template repeated.
 */

import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { HomeSEO } from '../components/SEO';
import Hero from '../components/Hero';
import GoogleReviews from '../components/GoogleReviews';
import { InstagramEmbed } from '../components/InstagramEmbed';
import Reveal from '../components/ui/Reveal';
import Button from '../components/ui/Button';
import FadeImage from '../components/ui/FadeImage';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import { SEO_DATA } from '../config/seoData';
import { EXTERNAL_URLS, BUSINESS_INFO, OPENING_HOURS_DISPLAY } from '../config/constants';

// `slug` must match the item's id in Menu.jsx's MENU_CATEGORIES exactly -
// it's how the card links to and highlights that exact dish on /menu.
const FEATURED_ITEMS = [
  {
    id: 1,
    slug: 'samosa-chaat',
    name: 'Samosa Chaat',
    description: 'Vegetable samosa topped with a saucy gravy, tamarind, sev, and pomegranates.',
    price: '6.49',
    image: '/images/popular-items/samosa-chaat.jpeg',
    category: 'Chaat',
    feature: true
  },
  {
    id: 2,
    slug: 'masala-chips',
    name: 'Masala Chips',
    description: 'Chips tossed with our in-house spicy masala sauce.',
    price: '4.99',
    image: '/images/popular-items/masala-chips.jpeg',
    category: 'Street Food'
  },
  {
    id: 3,
    slug: 'gup-shup-smash',
    name: 'Gup Shup Smash',
    description: 'Irish Angus beef, caramelised onions, cheese, gherkins, special sauce.',
    price: '7.99',
    image: '/images/popular-items/smash-burger.jpeg',
    category: 'Burgers'
  },
  {
    id: 4,
    slug: 'chaat-platter',
    name: 'Chaat Platter',
    description: 'Gol Gappe, Dahi Puri, Bhel Puri & Papri Chaat — built for sharing.',
    price: '14.99',
    image: '/images/popular-items/chaat-platter.jpeg',
    category: 'Chaat'
  },
  {
    id: 5,
    slug: 'chicken-65-chow-mein',
    name: 'Chicken 65 Chow Mein',
    description: 'Our Chennai Chicken 65, served with stir-fried noodles.',
    price: '9.99',
    image: '/images/popular-items/chicken-65-chow-mein.jpeg',
    category: 'Street Food'
  }
];

const WEEKDAY_HOURS = OPENING_HOURS_DISPLAY.slice(0, 5); // Monday – Friday
const WEEKEND_HOURS = OPENING_HOURS_DISPLAY.slice(5, 7); // Saturday – Sunday

const REWARD_POINTS = [
  { n: '01', title: 'Order like you always do', body: 'Dine in, collect, or order online — every purchase counts.' },
  { n: '02', title: 'Watch the points land', body: 'No card to carry, nothing to remember — it tracks itself.' },
  { n: '03', title: 'Cash in on the regulars’ menu', body: 'Free chai, birthday treats, and offers we only tell members about.' }
];

const Home = () => {
  return (
    <>
      <HomeSEO seoData={SEO_DATA.home} />

      <Hero />

      {/* ================= POPULAR PICKS — asymmetric feature grid ================= */}
      <section className="py-20 lg:py-28 bg-ink">
        <div className="container-custom">
          <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12 lg:mb-16">
            <div>
              <p className="eyebrow mb-4">Taste the authenticity</p>
              <h2 className="text-4xl lg:text-5xl text-cream max-w-lg">Our popular picks</h2>
            </div>
            <Button to="/menu" variant="text" arrow className="text-base">View full menu</Button>
          </Reveal>

          {/* Mobile: snap-scroll rail. Desktop: asymmetric bento grid */}
          <div className="flex lg:hidden gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5 hide-scrollbar">
            {FEATURED_ITEMS.map((item) => (
              <PickCard key={item.id} item={item} className="min-w-[78vw] aspect-[4/5] snap-start" />
            ))}
          </div>

          <div className="hidden lg:grid grid-cols-4 grid-rows-2 gap-5 h-[640px]">
            {FEATURED_ITEMS.map((item, i) => (
              <PickCard
                key={item.id}
                item={item}
                className={item.feature ? 'col-span-2 row-span-2' : 'col-span-1 row-span-1'}
                delay={i * 0.08}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ================= STORY — pull-quote + overlapping image spread ================= */}
      <section className="py-20 lg:py-28 bg-surface overflow-hidden">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Overlapping images */}
            <Reveal className="lg:col-span-5 relative" y={0}>
              <div className="relative aspect-[4/5] max-w-md mx-auto lg:mx-0">
                <FadeImage
                  src="/images/about/about-story.jpg"
                  alt="Inside Gup Shup, Collier Row — warm pendant lighting and terracotta booths"
                  className="absolute inset-0"
                  imgClassName="w-full h-full object-cover"
                />
                <div className="absolute -bottom-8 -right-8 sm:-right-10 w-2/5 aspect-square border-4 border-surface">
                  <FadeImage
                    src="/images/about/about-food.jpg"
                    alt="Freshly prepared chai and street food at Gup Shup"
                    className="block w-full h-full"
                    imgClassName="w-full h-full object-cover"
                  />
                </div>
              </div>
            </Reveal>

            {/* Copy */}
            <div className="lg:col-span-7 lg:pl-8">
              <Reveal delay={0.05}>
                <p className="eyebrow mb-6">Our story</p>
                <p className="font-display italic text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] text-cream mb-8">
                  &ldquo;Gup Shup means friendly conversation — that&rsquo;s exactly what we&rsquo;re about.&rdquo;
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="text-cream-dim font-sans leading-relaxed space-y-4 max-w-xl mb-8">
                  <p>
                    Since opening on Clockhouse Lane in Collier Row, we&rsquo;ve been brewing <strong className="text-cream">karak chai</strong> the
                    traditional way and cooking street food that runs from Lahore to Mumbai — samosas,
                    pakoras, chaat, all made fresh, all rooted in recipes passed down rather than shortcuts.
                  </p>
                  <p>
                    Founder Mohasin Khan built Gup Shup around a marriage of two food cultures, India and
                    Pakistan, and one idea: food is better when it&rsquo;s an excuse to sit, talk, and stay a while.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <Button to="/about-us" variant="outline" arrow>Read the full story</Button>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ================= REVIEWS ================= */}
      <GoogleReviews />

      {/* ================= VISIT US — full-bleed image band ================= */}
      <section className="relative bg-ink">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <Reveal y={0} className="relative h-[440px] sm:h-[520px] lg:h-auto lg:min-h-[760px] overflow-hidden">
            <VisitUsImage />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-ink/30" aria-hidden="true" />
          </Reveal>

          <div className="flex items-center py-20 sm:py-24 lg:py-28">
            <div className="px-6 sm:px-10 md:px-14 lg:pl-16 lg:pr-20 xl:pr-24 w-full max-w-2xl">
              <Reveal>
                <p className="eyebrow mb-5">3 Clockhouse Lane, Collier Row</p>
                <h2 className="text-4xl lg:text-5xl text-cream mb-7">Come find us</h2>
                <p className="text-cream-dim font-sans leading-relaxed mb-12 max-w-lg">
                  Free street parking, an easy run off the A12, and a room built for lingering.
                  Walk-ins are always welcome — but a table is one tap away if you&rsquo;d rather not wait.
                </p>
              </Reveal>

              <Reveal delay={0.1} className="mb-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-8 font-sans text-sm">
                  <div>
                    <p className="text-2xs uppercase tracking-widest2 text-cream-faint/70 mb-4">Mon – Fri</p>
                    <div className="space-y-3.5">
                      {WEEKDAY_HOURS.map((s) => (
                        <div key={s.day} className="flex items-baseline justify-between gap-4 border-b border-line pb-3">
                          <span className="text-cream-faint">{s.day}</span>
                          <span className="text-cream font-medium text-right whitespace-nowrap">{s.hours}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-2xs uppercase tracking-widest2 text-cream-faint/70 mb-4">Sat – Sun</p>
                    <div className="space-y-3.5">
                      {WEEKEND_HOURS.map((s) => (
                        <div key={s.day} className="flex items-baseline justify-between gap-4 border-b border-line pb-3">
                          <span className="text-cream-faint">{s.day}</span>
                          <span className="text-cream font-medium text-right whitespace-nowrap">{s.hours}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-cream-faint text-xs leading-relaxed mt-4">
                      Open UK bank holidays, 9:00 AM – 10:00 PM.
                    </p>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.2} className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
                <Button to="/location" variant="primary" arrow className="w-full">Plan your visit</Button>
                <Button href={EXTERNAL_URLS.reservation} variant="outline" className="w-full">Book a table</Button>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ================= REWARDS — solid colour block ================= */}
      <section className="py-20 lg:py-28 bg-amber-500 text-ink">
        <div className="container-custom">
          <Reveal className="max-w-2xl mb-14 lg:mb-16">
            <p className="font-sans text-2xs font-semibold uppercase tracking-widest2 text-ink/70 mb-4">Gup Shup Rewards</p>
            <h2 className="text-4xl lg:text-5xl mb-6">Regulars get treated like regulars</h2>
            <p className="font-sans text-ink/80 leading-relaxed">
              No punch card, no app to babysit. Just points that build every time you order — and a
              free cup waiting on your birthday.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-8 mb-14">
            {REWARD_POINTS.map((r, i) => (
              <Reveal key={r.n} delay={i * 0.1}>
                <span className="font-display text-5xl text-ink/30 block mb-4">{r.n}</span>
                <h3 className="text-xl font-display mb-2">{r.title}</h3>
                <p className="font-sans text-ink/75 text-sm leading-relaxed">{r.body}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <Button to="/rewards" variant="invert" arrow>See how it works</Button>
          </Reveal>
        </div>
      </section>

      {/* ================= SOCIALS ================= */}
      <section className="py-20 lg:py-28 bg-surface">
        <div className="container-custom">
          <Reveal className="text-center max-w-xl mx-auto mb-12">
            <p className="eyebrow mb-4">Join the conversation</p>
            <h2 className="text-4xl lg:text-5xl text-cream mb-4">Follow along</h2>
            <p className="text-cream-dim font-sans">@gupshupldn — behind-the-scenes, new specials, and the odd Gups-of-Chai carousel.</p>
          </Reveal>

          <Reveal delay={0.1} className="flex flex-col sm:flex-row justify-center items-center gap-6 flex-wrap mb-10">
            <InstagramEmbed src="/videos/gupshup-reel.mp4" />
            <InstagramEmbed src="/videos/gupshup-reel-2.mp4" />
            <InstagramEmbed src="/videos/gupshup-reel-4.mp4" />
          </Reveal>

          <Reveal delay={0.2} className="flex justify-center">
            <Button href="https://instagram.com/gupshupldn" variant="outline">@gupshupldn on Instagram</Button>
          </Reveal>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="py-24 lg:py-32 bg-ink text-center">
        <div className="container-custom">
          <Reveal>
            <p className="font-display italic text-2xl sm:text-3xl text-cream-dim mb-3">Chit-chat, over chai —</p>
            <h2 className="text-5xl lg:text-6xl text-cream mb-10">ready when you are.</h2>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button to="/order-online" variant="primary" arrow>Order Now</Button>
            <Button to="/location" variant="outline">{`Visit ${BUSINESS_INFO.address.area}`}</Button>
          </Reveal>
        </div>
      </section>
    </>
  );
};

/**
 * VisitUsImage - the storefront photo drifts a few px slower/faster than
 * the page as it scrolls through view, on top of FadeImage's own load-in
 * fade. Restrained to this one full-bleed non-hero image on the page (the
 * hero itself already gets a Ken Burns zoom from Slideshow) so it reads as
 * depth rather than being sprinkled everywhere. The moving layer is sized
 * taller than its own clipped container specifically so the drift never
 * exposes empty space at the top/bottom edge.
 */
const VisitUsImage = () => {
  const containerRef = useRef(null);
  const reduced = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [-24, 24]);

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden">
      <motion.div style={{ y }} className="absolute -inset-y-[6%] inset-x-0">
        <FadeImage
          src="/images/branding/storefront.jpg"
          alt="Gup Shup storefront on Clockhouse Lane, Collier Row, at dusk"
          className="h-full w-full"
          imgClassName="w-full h-full object-cover"
          style={{ objectPosition: '28% 30%' }}
        />
      </motion.div>
    </div>
  );
};

/**
 * PickCard - single menu-highlight card. `feature` items get larger type
 * and an eager-loaded image since they anchor the bento layout. The whole
 * card is one real, focusable link (a full-cover anchor layered over the
 * decorative image/badge/copy) rather than a clickable div, so it's
 * reachable by keyboard and picks up the site's global focus-visible ring
 * for free. It deep-links to the matching dish on /menu via a hash, which
 * Menu.jsx resolves to the right category, scrolls to and briefly
 * highlights.
 */
const PickCard = ({ item, className = '', delay = 0 }) => (
  <Reveal delay={delay} className={`relative overflow-hidden bg-card ${className}`}>
    {/* Framer Motion drives Reveal's own transform inline (its entrance
        y/opacity), which always beats a CSS class's transform on the same
        element - hover/press feedback has to live on this plain, un-animated
        wrapper instead or they'd silently never show up. :hover/:active
        (via Tailwind's `group`) cascade to ancestors of the hovered/pressed
        element, so both still fire from anywhere on the card, not just the
        overlay link. Tailwind's default variant order puts active: after
        hover: in the generated stylesheet, so a press always wins over a
        simultaneous hover rather than the two fighting over transform. */}
    <div className="group relative w-full h-full transition-[transform,filter,box-shadow] duration-300 ease-out hover:-translate-y-1 hover:shadow-lifted active:translate-y-0 active:scale-[0.98] active:brightness-90 active:duration-100">
      <FadeImage
        src={item.image}
        alt={`${item.name} — ${item.category} at Gup Shup, Collier Row`}
        className="absolute inset-0"
        imgClassName="w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" aria-hidden="true" />
      <span className={`absolute top-4 left-4 font-sans text-2xs uppercase tracking-widest2 px-2.5 py-1 ${item.feature ? 'bg-amber-500 text-ink' : 'bg-ink/60 backdrop-blur-sm text-cream border border-cream/20'}`}>
        {item.category}
      </span>
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className={`font-display text-cream ${item.feature ? 'text-2xl lg:text-3xl' : 'text-lg'}`}>{item.name}</h3>
        {item.feature && (
          <p className="text-cream-dim font-sans text-sm mt-2 max-w-xs">{item.description}</p>
        )}
      </div>
      <Link
        to={`/menu#${item.slug}`}
        className="absolute inset-0 rounded-sm"
        aria-label={`View ${item.name} on the menu`}
      />
    </div>
  </Reveal>
);

export default Home;
